import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';

export default function TermsPage() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <SEOHead
        titleES="Términos de Servicio — Colegio ARCE"
        titleEN="Terms of Service — Colegio ARCE"
        descES="Términos y condiciones de uso de los servicios de Colegio ARCE."
        descEN="Terms and conditions for using Colegio ARCE services."
        path="/terms"
      />
      <main className="pt-40 pb-20 px-6" data-testid="terms-page">
        <article className="max-w-3xl mx-auto prose prose-sm">
          <h1 className="text-3xl font-semibold text-[#1D1D1F] mb-8">
            {language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
          </h1>
          <p className="text-xs text-[#86868B] mb-8">
            {language === 'es' ? 'Última actualización: febrero 2026' : 'Last updated: February 2026'}
          </p>

          {language === 'es' ? (
            <div className="space-y-6 text-sm text-[#1D1D1F] leading-relaxed">
              <section>
                <h2 className="text-lg font-semibold mb-2">1. Descripción del servicio</h2>
                <p>Colegio ARCE (ARCE SPANISH SCHOOL, LLC.) es una escuela de español en línea con sede en Boston, Massachusetts. Ofrecemos clases virtuales de español en grupos reducidos para niños y adolescentes, siguiendo el currículo oficial del Ministerio de Educación de España.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">2. Condiciones de inscripción</h2>
                <p>La inscripción requiere una evaluación de nivel previa ($52). Todos los alumnos nuevos deben completar esta evaluación antes de ser asignados a un grupo. La inscripción se formaliza mediante la firma del contrato de matrícula y el pago correspondiente.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">3. Pagos y reembolsos</h2>
                <p>Los pagos se procesan de forma segura a través de Stripe, transferencia ACH o Zelle. Los precios publicados en el sitio web corresponden a la tarifa estándar (tarjeta). Se aplican descuentos por pago vía ACH (2%) o Zelle (3%). Una vez iniciado el curso, los pagos no son reembolsables. Las cancelaciones antes del inicio del trimestre o año escolar están sujetas a un cargo administrativo de $52.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">4. Descuento para hermanos</h2>
                <p>Las familias con más de un hijo inscrito reciben un 20% de descuento en la matrícula del segundo hijo en adelante.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">5. Propiedad intelectual</h2>
                <p>Todo el contenido del sitio web y los materiales educativos son propiedad de ARCE SPANISH SCHOOL, LLC. y están protegidos por derechos de autor. Queda prohibida su reproducción sin autorización.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">6. Limitación de responsabilidad</h2>
                <p>Colegio ARCE no se responsabiliza de interrupciones del servicio causadas por problemas técnicos de terceros (plataformas de videoconferencia, proveedores de internet). En caso de fuerza mayor, se reprogramarán las clases afectadas.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">7. Ley aplicable</h2>
                <p>Estos términos se rigen por las leyes del estado de Massachusetts, Estados Unidos. Cualquier disputa será resuelta en los tribunales competentes del condado de Suffolk, Massachusetts.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">8. Contacto</h2>
                <p>Para preguntas sobre estos términos, contáctanos en: <a href="mailto:laura@colegioarce.com" className="text-[#E63946] hover:underline">laura@colegioarce.com</a></p>
              </section>
            </div>
          ) : (
            <div className="space-y-6 text-sm text-[#1D1D1F] leading-relaxed">
              <section>
                <h2 className="text-lg font-semibold mb-2">1. Service Description</h2>
                <p>Colegio ARCE (ARCE SPANISH SCHOOL, LLC.) is an online Spanish school based in Boston, Massachusetts. We offer virtual Spanish classes in small groups for children and teenagers, following the official curriculum of the Spanish Ministry of Education.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">2. Enrollment Conditions</h2>
                <p>Enrollment requires a prior level assessment ($52). All new students must complete this assessment before being assigned to a group. Enrollment is formalized by signing the enrollment agreement and making the corresponding payment.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">3. Payments and Refunds</h2>
                <p>Payments are securely processed through Stripe, ACH bank transfer, or Zelle. Prices published on the website correspond to the standard rate (card). Discounts apply for payment via ACH (2%) or Zelle (3%). Once the course has started, payments are non-refundable. Cancellations before the start of the trimester or school year are subject to a $52 administrative fee.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">4. Sibling Discount</h2>
                <p>Families with more than one enrolled child receive a 20% discount on tuition for the second child onward.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">5. Intellectual Property</h2>
                <p>All website content and educational materials are the property of ARCE SPANISH SCHOOL, LLC. and are protected by copyright. Reproduction without authorization is prohibited.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">6. Limitation of Liability</h2>
                <p>Colegio ARCE is not responsible for service interruptions caused by third-party technical issues (video conferencing platforms, internet providers). In cases of force majeure, affected classes will be rescheduled.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">7. Governing Law</h2>
                <p>These terms are governed by the laws of the Commonwealth of Massachusetts, United States. Any disputes shall be resolved in the competent courts of Suffolk County, Massachusetts.</p>
              </section>
              <section>
                <h2 className="text-lg font-semibold mb-2">8. Contact</h2>
                <p>For questions about these terms, contact us at: <a href="mailto:laura@colegioarce.com" className="text-[#E63946] hover:underline">laura@colegioarce.com</a></p>
              </section>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}
